import { LocateFixed } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { BrowserMapAdapter } from './browserMapAdapter';

export default function OperationalMap({ points = [], encodedPolyline = null, followPointId = null, className = '' }) {
  const containerRef = useRef(null);
  const controllerRef = useRef(null);
  const [unavailable, setUnavailable] = useState(false);
  const adapter = useMemo(() => new BrowserMapAdapter(), []);
  const canRecenter = followPointId != null
    && points.some((point) => String(point?.id) === String(followPointId));
  const stateRef = useRef({ points, encodedPolyline, followPointId });
  stateRef.current = { points, encodedPolyline, followPointId };

  useEffect(() => {
    let disposed = false;
    adapter.renderOperationalMap(containerRef.current, stateRef.current)
      .then((controller) => {
        if (disposed) controller.destroy();
        else {
          controllerRef.current = controller;
          controller.update(stateRef.current);
        }
      })
      .catch(() => { if (!disposed) setUnavailable(true); });
    return () => {
      disposed = true;
      controllerRef.current?.destroy();
      controllerRef.current = null;
    };
  }, [adapter]);

  useEffect(() => {
    controllerRef.current?.update({ points, encodedPolyline, followPointId });
  }, [encodedPolyline, followPointId, points]);

  if (unavailable) {
    return <div className={`grid min-h-56 place-items-center rounded-2xl bg-stone-100 p-5 text-center text-sm text-stone-600 dark:bg-white/5 dark:text-stone-300 ${className}`}>Map preview is unavailable. Navigation and delivery details remain available.</div>;
  }
  return <div className={`relative min-h-56 overflow-hidden rounded-2xl bg-stone-100 dark:bg-white/5 ${className}`}>
    <div ref={containerRef} className="absolute inset-0" role="img" aria-label="Delivery route map" />
    {canRecenter && <button type="button" onClick={() => controllerRef.current?.recenter()} className="absolute bottom-4 right-4 z-10 inline-flex min-h-11 items-center gap-2 rounded-full bg-white px-4 text-sm font-semibold text-stone-900 shadow-lg ring-1 ring-black/10 hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:bg-neutral-900 dark:text-white dark:ring-white/15 dark:hover:bg-neutral-800" aria-label="Recenter map on my location"><LocateFixed className="h-4 w-4" aria-hidden="true" /> Recenter</button>}
  </div>;
}
